import type {
  CompanyCardDto,
  ConfigurationReviewDto,
  EconomicActivityDto,
  EquipmentDto,
  EquipmentState,
  IdealAvoidedDto,
  IdealDesignDto,
  IdealItemDto,
  IdealQuestionDto,
  OnboardingState,
  Site,
  StepGuideDto,
  WizardStepDto,
  WizardStepKey,
} from '@bk/contracts';
import { isBudgetError, isCreditMessage, isEngineUnavailable, ownerError } from '@bk/contracts';
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { api, errorMessage } from '../../../lib/api';
import { useTenant, useTenantQuery } from '../../../lib/useTenant';
import { useSetupProgress } from '../Setup';
import { SamePairs, stepOpenKey } from './StepReview';
import { useTalk } from './Talk';
import { AddedItems } from './AddedItems';
import { StepTime } from './StepTimeline';
import './consulente.css';

/**
 * La pagina del consulente (ADR 0146): ogni passo nella forma delle pagine prototipo di Boostar e Hoteligence. In alto
 * la partita IVA, il nome «al suo meglio» con i numeri e la legenda; la barra dei passi; poi il passo con il suo
 * paragrafo, le voci a gruppi con il nome vero, la voce standard e l'etichetta, le domande con l'effetto di ogni
 * risposta. In fondo alla configurazione il confronto con la visura e «Cosa ho evitato».
 */

type Tag = 'law' | 'good' | 'grow' | 'real';
const TAG_TEXT: Record<Tag, string> = { law: 'Obbligatorio', good: 'Buona prassi', grow: 'Per crescere', real: 'Dal web' };

/** L'etichetta che si vede: la stessa base del server (labelOf); senza base una voce è buona prassi. */
export function tagOf(v: Pick<IdealItemDto, 'base' | 'obbligatorio'>): Tag {
  const base = v.base ?? (v.obbligatorio ? 'legge' : 'prassi');
  return base === 'legge' ? 'law' : base === 'crescere' ? 'grow' : base === 'web' ? 'real' : 'good';
}

function TagPill({ tag, text }: { tag: Tag; text?: string }) {
  return <span className={`bkc-tag ${tag}`}>{text ?? TAG_TEXT[tag]}</span>;
}

const euro = (n: number) =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toLocaleString('it-IT', { maximumFractionDigits: 1 })} M€`
    : `${Math.round(n).toLocaleString('it-IT')} €`;

/** Il sito di una pagina, per citarla in breve; l'indirizzo intero se non si legge. */
const hostOf = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

/**
 * Il nome con cui si chiama l'azienda (ADR 0146): la ragione sociale senza la forma giuridica e, se è tutta in maiuscolo,
 * con le iniziali maiuscole. «BOOSTAR SOCIETA' A RESPONSABILITA' LIMITATA» diventa «Boostar».
 */
export function shortName(full: string | null): string | null {
  if (!full) return null;
  const forms =
    /\s*(,\s*)?\b(societ[aà]'?\s+(a\s+responsabilit[aà]'?\s+limitata(\s+semplificata)?|per\s+azioni|in\s+accomandita\s+semplice|in\s+nome\s+collettivo|cooperativa)|s\.?\s?r\.?\s?l\.?(\s?s\.?)?|s\.?\s?p\.?\s?a\.?|s\.?\s?a\.?\s?s\.?|s\.?\s?n\.?\s?c\.?|soc\.?\s?coop\.?)\s*$/i;
  const bare = full.replace(forms, '').trim() || full.trim();
  if (bare !== bare.toUpperCase()) return bare;
  return bare.toLowerCase().replace(/(^|[\s'’-])(\p{L})/gu, (_, a: string, b: string) => a + b.toUpperCase());
}

const year = (d: string | null) => (d ? d.slice(0, 4) : null);

/** Una riga del progetto nella forma dei prototipi: nome vero, etichetta, voce standard, perché e fonte. */
export interface Row {
  key: string;
  t: ReactNode;
  tag?: Tag;
  tagText?: string;
  std?: ReactNode;
  d?: ReactNode;
  src?: Array<{ titolo: string; url: string | null }>;
}

export function Item({ r }: { r: Row }) {
  return (
    <div className="bkc-item">
      <span className="t">{r.t}</span>
      {r.tag ? <TagPill tag={r.tag} text={r.tagText} /> : <span />}
      {r.std && <span className="std">{r.std}</span>}
      {r.d && <span className="d">{r.d}</span>}
      {!!r.src?.length && (
        <span className="src">
          Fonte:{' '}
          {r.src.map((s, k) => (
            <span key={s.titolo + k}>
              {k > 0 && ' · '}
              {s.url ? (
                <a href={s.url} target="_blank" rel="noreferrer">
                  {s.titolo}
                </a>
              ) : (
                s.titolo
              )}
            </span>
          ))}
        </span>
      )}
    </div>
  );
}

export function Group({ title, note, rows, action }: { title: ReactNode; note?: ReactNode; rows: Row[]; action?: ReactNode }) {
  if (!rows.length) return null;
  return (
    <section className="bkc-group">
      <div className="bkc-group-head">
        <h3 style={{ font: 'inherit', textTransform: 'inherit' }}>{title}</h3>
        {(note || action) && (
          <span>
            {note}
            {note && action && ' · '}
            {action}
          </span>
        )}
      </div>
      {rows.map((r) => (
        <Item key={r.key} r={r} />
      ))}
    </section>
  );
}

/** Da una voce del progetto alla riga: il nome dell'azienda in grassetto, sotto la voce standard e il riferimento. */
export function rowOf(v: IdealItemDto, k: number | string): Row {
  const ref = v.riferimento ?? v.norma ?? null;
  const std = [v.standard ?? null, !v.standard ? ref : null, v.quantita ?? null].filter(Boolean).join(' · ');
  const d = v.perche && v.perche !== v.descrizione ? v.perche : v.descrizione;
  return {
    key: `${v.nome}-${k}`,
    t: v.nome,
    tag: tagOf(v),
    std: std || undefined,
    d: d || undefined,
    src: v.fonti?.map((f) => ({ titolo: f.titolo, url: f.url })) ?? undefined,
  };
}

/** Le voci a gruppi, come le scrive il cervello: senza gruppo vanno sotto il titolo di riserva. */
function byGroup(voci: IdealItemDto[], fallback: string) {
  const out = new Map<string, IdealItemDto[]>();
  for (const v of voci) {
    const g = v.gruppo?.trim() || fallback;
    out.set(g, [...(out.get(g) ?? []), v]);
  }
  return [...out.entries()];
}

/* ------------------------------------------------------------------ la testa e la barra dei passi */

export function ConsultantTop({
  steps,
  at,
  sealed,
}: {
  steps: WizardStepDto[];
  at: number;
  sealed: string | null;
}) {
  const card = useTenantQuery<CompanyCardDto>(['company'], '/company');
  const sites = useTenantQuery<{ data: Site[] }>(['sites'], '/sites');
  const id = card.data?.identita;
  const name = shortName(id?.ragione_sociale ?? null) ?? 'La tua azienda';
  const docs = card.data?.documenti_di_partenza;
  const live = (sites.data?.data ?? []).filter((s) => !s.tolta && s.status !== 'chiusa');
  const facts: Array<[string, string]> = [];
  if (id?.fatturato) facts.push([euro(id.fatturato), `ricavi${id.anno_bilancio ? ` ${id.anno_bilancio}` : ''}`]);
  if (id?.addetti) facts.push([String(id.addetti), `addetti${id.addetti_al ? ` al ${id.addetti_al.split('-').reverse().join('/')}` : ''}`]);
  if (live.length) facts.push([String(live.length), live.length === 1 ? 'sede' : 'sedi, dalla visura e dal web']);
  if (id?.ateco_primario) facts.push([id.ateco_primario.split(/\s|-/)[0] ?? id.ateco_primario, 'codice ATECO prevalente']);
  if (id?.data_costituzione) facts.push([year(id.data_costituzione)!, 'anno di costituzione']);
  return (
    <>
      <div className="bkc-note">
        Timonio ha costruito questa configurazione partendo dalla partita IVA: la visura, il bilancio e le pagine
        pubbliche dell’azienda. Ogni voce dice perché c’è e da dove viene; le persone arrivano dopo, nei ruoli.
      </div>
      {id?.partita_iva && (
        <div className="bkc-input">
          <span>Partita IVA inserita</span>
          <span className="bkc-vat">{id.partita_iva}</span>
          <span>→ {[docs?.visura ? 'visura' : null, docs?.bilancio ? 'bilancio' : null, 'pagine verificate dell’azienda'].filter(Boolean).join(', ')}</span>
        </div>
      )}
      <header className="bkc-hero">
        <h1>{name}, al suo meglio</h1>
        {id?.in_breve && <p>{id.in_breve}</p>}
        {facts.length > 0 && (
          <div className="bkc-facts">
            {facts.map(([b, s]) => (
              <div key={s} className="bkc-fact">
                <b>{b}</b>
                <span>{s}</span>
              </div>
            ))}
          </div>
        )}
        <div className="bkc-legend">
          Perché c’è ogni voce:
          <TagPill tag="law" /> lo chiede la legge
          <TagPill tag="good" /> lo fanno le aziende come questa
          <TagPill tag="real" /> nome trovato online
        </div>
      </header>
      <nav className="bkc-steps" aria-label="Le fasi della configurazione">
        {steps.map((s, k) => (
          <span
            key={s.chiave}
            className={`bkc-chip ${k < at ? 'done' : k > at ? 'later' : ''}`}
            aria-current={k === at ? 'step' : undefined}
          >
            {k < at ? '✓ ' : ''}
            {s.titolo}
            <StepTime t={s.tempi} at={k < at ? 'prima' : k === at ? 'qui' : 'dopo'} />
          </span>
        ))}
        <span className="bkc-pct" aria-live="polite">
          {sealed ? `✓ «${sealed}» è chiusa · ` : ''}
          {Math.round((at / Math.max(1, steps.length)) * 100)}% fatto
        </span>
      </nav>
    </>
  );
}

/* ------------------------------------------------------------------ le domande */

/**
 * Le domande del passo nella forma dei prototipi: al massimo 5, ognuna con l'effetto di ogni risposta e la più probabile
 * già scelta; le scelte del consulente scritte come sue. La risposta va in «Parliamone» come messaggio della persona.
 */
export function Questions({ step, domande, rethinking }: { step: WizardStepKey; domande: IdealQuestionDto[]; rethinking?: boolean }) {
  const { t, tenant, isManager } = useTenant();
  const qc = useQueryClient();
  const progress = useSetupProgress();
  const opening = useIsMutating({ mutationKey: stepOpenKey(tenant.id, step) }) > 0;
  const lavori = progress.data?.wizard.find((w) => w.chiave === step)?.lavori ?? [];
  const busy = rethinking ?? lavori.some((l) => /Progetto|Compilo sedi/.test(l));
  const [answered, setAnswered] = useState<Record<string, string>>({});
  const answer = useMutation({
    mutationFn: (v: { domanda: string; risposta: string }) =>
      api<OnboardingState>(t('/onboarding/messages'), { body: { message: `${v.domanda} ${v.risposta}`, passo: step } }),
    onSuccess: (next) => {
      qc.setQueryData(['tenant', tenant.id, 'onboarding'], next);
      if (step === 'impianti') void qc.invalidateQueries({ queryKey: ['tenant', tenant.id, 'equipment'] });
    },
  });
  const asked = domande.filter((q) => !q.scelta);
  const chosen = domande.filter((q) => q.scelta);
  if (opening) return <SamePairs step={step} />;
  if (busy)
    return (
      <>
        <SamePairs step={step} />
        <p className="bkc-busy" role="status">
          <i aria-hidden /> Rivedo le domande con le ultime novità: compaiono qui appena sono pronte.
        </p>
      </>
    );
  const rest = asked.filter((q) => !answered[q.domanda] && q.probabile);
  return (
    <div className="bkc-qs">
      <SamePairs step={step} />
      {asked.map((q) => {
        const mine = answered[q.domanda];
        return (
          <div key={q.domanda} className="bkc-q">
            <div className="qt">{q.domanda}</div>
            {q.perche && <div className="why">{q.legge ? 'Riguarda la legge. ' : ''}{q.perche}</div>}
            <div className="bkc-opts" role="radiogroup" aria-label={q.domanda}>
              {q.risposte.map((r, k) => {
                const on = mine ? mine === r : q.probabile === r;
                return (
                  <button
                    key={r}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    className={`bkc-opt ${on ? 'on' : ''}`}
                    disabled={!isManager || !!mine || answer.isPending}
                    onClick={() => {
                      setAnswered((a) => ({ ...a, [q.domanda]: r }));
                      answer.mutate({ domanda: q.domanda, risposta: r });
                    }}
                  >
                    <span className="dot" aria-hidden />
                    <span className="ol">
                      {r}
                      {!mine && q.probabile === r && <span className="bkc-prob">più probabile</span>}
                    </span>
                    {q.effetti?.[k] && <span className="oe">{q.effetti[k]}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
      {isManager && rest.length > 0 && (
        <button
          type="button"
          className="bkc-link"
          disabled={answer.isPending}
          onClick={() => {
            setAnswered((a) => ({ ...a, ...Object.fromEntries(rest.map((q) => [q.domanda, q.probabile!])) }));
            answer.mutate({ domanda: 'Basta, decidi tu:', risposta: `\n${rest.map((q) => `${q.domanda} ${q.probabile}`).join('\n')}` });
          }}
        >
          Basta, decidi tu: valgono le risposte più probabili
        </button>
      )}
      {answer.error && <p className="bkc-noq">{errorMessage(answer.error)}</p>}
      {chosen.length > 0 && (
        <Group
          title="Le scelte che ho fatto io"
          note="con la risposta più probabile: se una non è così, dimmelo in «Parliamone»"
          rows={chosen.map((q, k) => {
            const i = q.probabile ? q.risposte.indexOf(q.probabile) : -1;
            return { key: `c${k}`, t: q.domanda, tag: 'real' as Tag, tagText: q.probabile ?? '', d: i >= 0 ? q.effetti?.[i] : undefined };
          })}
        />
      )}
      {asked.length === 0 && (
        <p className="bkc-noq">Nessuna domanda per te in questo passo: le risposte non cambierebbero il progetto.</p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ un passo */

export function StepFrame({
  n,
  of,
  title,
  text,
  children,
}: {
  n: number;
  of: number;
  title: string;
  text: string | null | undefined;
  children: ReactNode;
}) {
  return (
    <section className="bkc-step">
      <div className="bkc-step-head">
        <span className="n">
          Passo {n} di {of}
        </span>
        <h2>{title}</h2>
        {text && <p>{text}</p>}
      </div>
      {children}
    </section>
  );
}

/** Il progetto di un passo generico (programmi, organizzazione, procedure): l'impresa a gruppi, poi sede per sede. */
function DesignGroups({ d, fallback, sites }: { d: IdealDesignDto; fallback: string; sites: Site[] }) {
  const talk = useTalk();
  const siteName = (id: string, sede: string) => sites.find((s) => s.id === id)?.name ?? sede;
  return (
    <>
      {byGroup(d.impresa?.voci ?? [], fallback).map(([g, voci]) => (
        <Group key={g} title={g} rows={voci.map(rowOf)} />
      ))}
      {d.sedi.map((s) => {
        const voci = [...(s.sede_in_se?.voci ?? []), ...s.attivita.flatMap((a) => a.voci)];
        return (
          <Group
            key={s.sede_id}
            title={`${siteName(s.sede_id, s.sede)}${s.luogo ? ` · ${s.luogo}` : ''}`}
            note={s.addetti ? `${s.addetti} addetti` : undefined}
            action={
              <button type="button" className="bkc-link" style={{ fontSize: 12.5 }} onClick={() => talk.open(`Su ${siteName(s.sede_id, s.sede)}: `)}>
                Correggi
              </button>
            }
            rows={voci.map(rowOf)}
          />
        );
      })}
    </>
  );
}

/* ------------------------------------------------------------------ sedi e attività */

function SiteFacts({ sites, d }: { sites: Site[]; d: IdealDesignDto | null }) {
  const talk = useTalk();
  const live = sites.filter((s) => !s.tolta);
  return (
    <>
      {live.map((s) => {
        const ideal = d?.sedi.find((x) => x.sede_id === s.id);
        const rows: Row[] = [
          {
            key: 'chi',
            t: s.sintesi ?? s.activity ?? s.name,
            tag: s.visura ? 'law' : 'real',
            tagText: s.visura ? (s.legal ? 'Sede legale' : 'Dalla visura') : 'Dal web',
            std: [s.visura, s.ateco_primary, ...s.ateco_secondary].filter(Boolean).join(' · ') || undefined,
            d: s.in_breve.length ? s.in_breve.map((x) => x.testo).join(' · ') : undefined,
            src: s.in_breve.filter((x) => x.url).map((x) => ({ titolo: hostOf(x.url!), url: x.url })),
          },
          ...(s.allo_stesso_indirizzo.length
            ? [
                {
                  key: 'altri',
                  t: `Allo stesso indirizzo: ${s.allo_stesso_indirizzo.map((a) => a.nome).join(', ')}`,
                  tag: 'real' as Tag,
                  tagText: 'Un’altra impresa',
                  d: 'Non entrano nel progetto: il consulente ne tiene conto per rapporti, spazi e sicurezza condivisi.',
                },
              ]
            : []),
          ...(ideal?.sede_in_se?.voci ?? []).map(rowOf),
        ];
        const addetti = s.employees ?? s.addetti_comune?.addetti ?? null;
        return (
          <Group
            key={s.id}
            title={`${s.name}${s.address ? ` · ${s.address}` : ''}${s.municipality && !(s.address ?? '').includes(s.municipality) ? `, ${s.municipality}` : ''}`}
            note={addetti ? `${addetti} addetti${s.employees ? '' : ' nel comune'}` : undefined}
            action={
              <button type="button" className="bkc-link" style={{ fontSize: 12.5 }} onClick={() => talk.open(`Su ${s.name}: `)}>
                Correggi
              </button>
            }
            rows={rows}
          />
        );
      })}
    </>
  );
}

function ActivityGroups({ sites, acts, d }: { sites: Site[]; acts: EconomicActivityDto[]; d: IdealDesignDto | null }) {
  const live = sites.filter((s) => !s.tolta);
  return (
    <>
      {live.map((s) => {
        const here = acts
          .flatMap((a) => a.in_sede.filter((x) => x.site_id === s.id && x.stato !== 'esclusa').map((x) => ({ a, x })))
          .sort((p, q) => (q.x.peso ?? 0) - (p.x.peso ?? 0));
        const ideal = d?.sedi.find((x) => x.sede_id === s.id);
        const rows: Row[] = [
          ...here.map(({ a, x }) => ({
            key: a.id,
            t: `${a.icona ? `${a.icona} ` : ''}${a.nome}`,
            tag: (a.fonte === 'visura' || a.fonte === 'registro' ? 'law' : 'real') as Tag,
            tagText: a.fonte === 'visura' || a.fonte === 'registro' ? 'Dalla visura' : a.fonte === 'online' || a.fonte === 'sito' ? 'Dal web' : a.fonte === 'persona' ? 'Detta da te' : 'Proposta',
            std: [a.ateco, x.peso != null ? `${x.peso}% del lavoro` : null].filter(Boolean).join(' · ') || undefined,
            d: x.descrizione ?? a.descrizione ?? undefined,
          })),
          ...(ideal?.attivita ?? []).flatMap((a) => a.voci).map(rowOf),
        ];
        return <Group key={s.id} title={s.name} note={s.municipality ?? undefined} rows={rows} />;
      })}
    </>
  );
}

/* ------------------------------------------------------------------ impianti come registro */

function EquipmentRegister({ eq, sites }: { eq: EquipmentState; sites: Site[] }) {
  const items = eq.data.filter((e) => e.stato !== 'non_presente');
  const bySite = new Map<string, EquipmentDto[]>();
  for (const e of items) bySite.set(e.sede ?? 'azienda', [...(bySite.get(e.sede ?? 'azienda') ?? []), e]);
  const law = (e: EquipmentDto) => e.obblighi.some((o) => (o.base ?? 'legge') === 'legge');
  const timed = items.filter((e) => e.obblighi.some((o) => o.periodicita)).length;
  const name = (id: string) => (id === 'azienda' ? 'Per tutta l’impresa' : sites.find((s) => s.id === id)?.name ?? 'Sede');
  const weight = (e: EquipmentDto) => (law(e) ? 0 : 1);
  return (
    <>
      <div className="bkc-sum">
        <div>
          <b>{items.length}</b>
          <span>impianti, macchinari e dotazioni</span>
        </div>
        <div>
          <b>{items.filter(law).length}</b>
          <span>con un obbligo di legge</span>
        </div>
        <div>
          <b>{timed}</b>
          <span>con un controllo a scadenza</span>
        </div>
        <div>
          <b>{bySite.size}</b>
          <span>{bySite.size === 1 ? 'sede con impianti' : 'sedi con impianti'}</span>
        </div>
      </div>
      {[...bySite.entries()].map(([id, list]) => (
        <section key={id} className="bkc-group">
          <div className="bkc-group-head">
            <h3 style={{ font: 'inherit', textTransform: 'inherit' }}>{name(id)}</h3>
            <span>{list.length} voci</span>
          </div>
          <div className="bkc-tbl">
            <table className="bkc-reg">
              <thead>
                <tr>
                  <th>Impianto o macchinario</th>
                  <th>Quanti</th>
                  <th>Perché</th>
                  <th>Cosa si fa</th>
                  <th>Ogni quanto</th>
                  <th>Chi</th>
                </tr>
              </thead>
              <tbody>
                {[...list].sort((a, b) => weight(a) - weight(b)).map((e) => {
                  const o = e.obblighi;
                  const first = o.find((x) => (x.base ?? 'legge') === 'legge') ?? o[0];
                  return (
                    <tr key={e.id}>
                      <td>
                        {e.icona ? `${e.icona} ` : ''}
                        {e.nome}
                        {e.perche && <small>{e.perche}</small>}
                      </td>
                      <td className="n">
                        {e.quantita ?? e.stima ?? '—'}
                        {e.quantita == null && e.stima && <small>stima, la correggi tu</small>}
                      </td>
                      <td>
                        <TagPill tag={law(e) ? 'law' : 'good'} />
                        {first?.norma && <small>{first.norma}</small>}
                      </td>
                      <td>{o.map((x) => x.descrizione).filter(Boolean).join(' · ') || '—'}</td>
                      <td>{[...new Set(o.map((x) => x.periodicita).filter(Boolean))].join(' · ') || '—'}</td>
                      <td>{[...new Set(o.map((x) => x.chi).filter(Boolean))].join(' · ') || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ))}
      {!items.length && <p className="bkc-noq">Nessun impianto: le sedi sono solo indirizzi o uffici senza dotazioni da controllare.</p>}
    </>
  );
}

/* ------------------------------------------------------------------ il fondo: confronto con la visura e cosa ho evitato */

const ERROR_NAME: Record<IdealAvoidedDto['tipo'], string> = {
  fatto: 'Errore di fatto',
  identita: 'Errore di identità',
  legge: 'Errore di legge',
  peso: 'Errore di peso',
};

export function ConfigurationReview() {
  const review = useTenantQuery<ConfigurationReviewDto>(['configuration-review'], '/onboarding/configuration-review', {
    refetchInterval: (d) => (!d || d.generating ? 4000 : false),
  });
  const r = review.data;
  if (!r) return null;
  return (
    <>
      <section className="bkc-step">
        <div className="bkc-step-head">
          <span className="n">In background</span>
          <h2>Confronto con la visura</h2>
          <p>
            Mentre leggevi, ho confrontato ciò che ho trovato online con la visura. Sui dati legali vince la visura, e i
            passi si sono riallineati senza disturbarti.
          </p>
        </div>
        {r.generating && !r.confronto.length ? (
          <p className="bkc-busy" role="status">
            <i aria-hidden /> Confronto il web con la visura…
          </p>
        ) : r.confronto.length ? (
          <div className="bkc-tbl">
            <table>
              <thead>
                <tr>
                  <th>Cosa</th>
                  <th>Dal web</th>
                  <th>La visura decide</th>
                  <th>Cosa cambia</th>
                </tr>
              </thead>
              <tbody>
                {r.confronto.map((c) => (
                  <tr key={c.cosa}>
                    <td>{c.cosa}</td>
                    <td>{c.dal_web}</td>
                    <td>{c.visura}</td>
                    <td className="ok">{c.cambia}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="bkc-noq">Il web e la visura dicono le stesse cose: niente da riallineare.</p>
        )}
      </section>
      <section className="bkc-step">
        <div className="bkc-step-head">
          <h2>Cosa ho evitato</h2>
        </div>
        {r.evitati.length ? (
          <Group
            title="Errori trovati e corretti prima di mostrarti i passi"
            rows={r.evitati.map((e, k) => ({ key: `e${k}`, t: e.cosa, std: ERROR_NAME[e.tipo], d: e.perche }))}
          />
        ) : (
          <p className="bkc-noq">Nessun errore da correggere: le fonti erano coerenti.</p>
        )}
      </section>
      {review.error && <p className="bkc-noq">{errorMessage(review.error)}</p>}
    </>
  );
}

/* ------------------------------------------------------------------ la pagina del passo */

/** Cosa progetta ogni passo, se il cervello non ha ancora scritto il suo paragrafo. */
const FALLBACK_TEXT: Partial<Record<WizardStepKey, string>> = {
  sedi: 'Dove lavora l’azienda, come lo dice la visura e come la riconoscono i clienti.',
  attivita: 'Cosa si fa in ogni sede, con il peso di ogni attività e ciò che ognuna chiede.',
  impianti: 'Tutto ciò che va tenuto in efficienza: quanti sono, cosa chiede la legge, ogni quanto e chi se ne occupa.',
  software: 'Per ogni area del lavoro il programma che serve; dove si vede da fuori, il nome di quello che usate già.',
  organizzazione: 'I ruoli che servono, con i ruoli di legge e chi lavora da fuori.',
  procedure: 'Come si fanno le cose che contano, con chi le fa e ogni quanto.',
};
const FALLBACK_GROUP: Partial<Record<WizardStepKey, string>> = {
  software: 'Per tutta l’impresa',
  organizzazione: 'Per tutta l’impresa',
  procedure: 'Per tutta l’impresa',
};


/** Le voci che il titolare aggiunge a mano (ADR 0098): restano sue, il consulente le completa. */
const ADD: Partial<Record<WizardStepKey, { intro: string; label: string; placeholder: string; note: string; feminine?: boolean }>> = {
  software: {
    intro: 'Se usate un programma che non ho trovato, o ve ne serve un altro, aggiungilo: resta tuo, e io lo completo e lo collego agli altri.',
    label: 'Aggiungi un programma',
    placeholder: 'Es. il programma delle paghe o il gestionale che usate',
    note: 'Es. lo usa l’amministrazione per le fatture',
  },
  organizzazione: {
    intro: 'Se c’è un ruolo che qui non vedi, o te ne serve uno, aggiungilo: resta tuo, e io lo completo con cosa fa, a chi risponde e con quali programmi lavora.',
    label: 'Aggiungi un ruolo',
    placeholder: 'Es. responsabile commerciale',
    note: 'Es. segue i clienti grandi e le offerte',
  },
  procedure: {
    intro: 'Se fate qualcosa che qui non vedi, aggiungi la procedura: resta tua, e io la completo con chi la fa, ogni quanto e cosa lascia.',
    label: 'Aggiungi una procedura',
    placeholder: 'Es. l’inventario di fine anno',
    note: 'Es. lo fa il magazzino ogni dicembre',
    feminine: true,
  },
};

/** Il passo nella forma dei prototipi. `n`/`of` sono la posizione nel percorso. */
/**
 * Il passo non è riuscito (ADR 0146, 0150): l'errore del progetto (dell'impresa, di una sede, degli impianti) o un passo
 * di progetto senza voci. Una regola sola per la pagina del passo e per i pulsanti in fondo: un passo fallito non si
 * giudica e non si lascia come se fosse pronto. `engine`: il motore AI non rispondeva, il server lo rifà da solo.
 */
export function useStepFailure(step: WizardStepKey, enabled = true) {
  const guide = useTenantQuery<StepGuideDto>(['guide', step], `/onboarding/guide/${step}`, { enabled });
  const eq = useTenantQuery<EquipmentState>(['equipment'], '/company/equipment', { enabled: enabled && step === 'impianti' });
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const redo = useMutation({
    mutationFn: () => api<StepGuideDto>(t(`/onboarding/guide/${step}/refresh`), { method: 'POST' }),
    onSuccess: (g) => {
      qc.setQueryData(['tenant', tenant.id, 'guide', step], g);
      void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
    },
  });
  const d = guide.data?.ideale ?? null;
  const errors = [
    ...(d ? [d.impresa?.error, ...d.sedi.map((x) => x.error)] : [guide.data?.error]),
    ...(step === 'impianti' ? Object.values(eq.data?.errori ?? {}) : []),
  ].filter((e): e is string => !!e);
  const voci = d ? (d.impresa?.voci.length ?? 0) + d.sedi.reduce((n, x) => n + (x.sede_in_se?.voci.length ?? 0) + x.attivita.reduce((m, a) => m + a.voci.length, 0), 0) : 0;
  const designStep = step === 'software' || step === 'organizzazione' || step === 'procedure';
  const empty = designStep && !!guide.data && voci === 0 && !d?.impresa?.generating;
  const failed = enabled && (errors.length > 0 || empty);
  return {
    failed,
    empty,
    errors,
    budget: errors.some(isBudgetError),
    // Il motore AI non rispondeva: il server rifà il passo da solo appena un motore risponde.
    engine: errors.length > 0 && errors.every(isEngineUnavailable),
    redo,
  };
}

/** Il passo nella forma dei prototipi. `n`/`of` sono la posizione nel percorso. */
export function ConsultantStep({ step, title, n, of, last }: { step: WizardStepKey; title: string; n: number; of: number; last: boolean }) {
  const guide = useTenantQuery<StepGuideDto>(['guide', step], `/onboarding/guide/${step}`);
  const sites = useTenantQuery<{ data: Site[] }>(['sites'], '/sites');
  const card = useTenantQuery<CompanyCardDto>(['company'], '/company', { enabled: step === 'attivita' });
  const eq = useTenantQuery<EquipmentState>(['equipment'], '/company/equipment', {
    enabled: step === 'impianti',
    // Anche con un errore del motore AI spento: il server rifà la sede da solo e la pagina lo vede arrivare.
    refetchInterval: (d) =>
      d && (d.in_preparazione.length || d.in_completamento?.length) ? 4000 : d && Object.values(d.errori).some(isEngineUnavailable) ? 30_000 : false,
  });
  const d = guide.data?.ideale ?? null;
  const s = sites.data?.data ?? [];
  const { tenant, isManager } = useTenant();
  const qc = useQueryClient();
  const { failed, errors, budget, engine, redo } = useStepFailure(step);
  // Il motore AI non rispondeva (ADR 0151): ogni mezzo minuto si rilegge il passo, che il server intanto rifà da solo.
  useEffect(() => {
    if (!engine) return;
    const id = setInterval(() => void qc.invalidateQueries({ queryKey: ['tenant', tenant.id, 'guide', step] }), 30_000);
    return () => clearInterval(id);
  }, [engine, qc, tenant.id, step]);
  const domande =
    step === 'impianti' ? Object.values(eq.data?.domande ?? {}).flat().slice(0, 5) : (d?.domande ?? []);
  return (
    <div style={{ display: 'grid', gap: 28 }}>
      <StepFrame n={n} of={of} title={title} text={d?.apertura ?? FALLBACK_TEXT[step]}>
        {step === 'sedi' ? (
          <SiteFacts sites={s} d={d} />
        ) : step === 'attivita' ? (
          <ActivityGroups sites={s} acts={card.data?.attivita ?? []} d={d} />
        ) : step === 'impianti' ? (
          eq.data ? <EquipmentRegister eq={eq.data} sites={s} /> : null
        ) : d ? (
          <DesignGroups d={d} fallback={FALLBACK_GROUP[step] ?? 'Per tutta l’impresa'} sites={s} />
        ) : null}
        {failed && (
          <section className="bkc-q" role={engine ? 'status' : 'alert'} style={{ borderColor: engine ? 'var(--bkc-accent)' : 'var(--bkc-law)' }}>
            <div className="qt">
              {budget
                ? 'Il consulente si è fermato al tetto di spesa di questa azienda'
                : engine
                  ? 'Il consulente riprende questo passo da solo'
                  : 'Il progetto di questo passo non è riuscito'}
            </div>
            <div className="why" style={{ marginTop: 0 }}>
              {budget
                ? 'Il cervello ha raggiunto il tetto mensile di spesa del motore AI fissato per questa azienda: le voci di questo passo non sono state scritte. Si riparte alzando il tetto o il mese prossimo.'
                : engine
                  ? `${isCreditMessage(errors[0]) ? errors[0] : 'Il motore AI non ha potuto lavorare in quel momento.'} Riprovo da solo ogni due minuti e il passo compare qui appena è pronto: puoi aspettare o rifarlo subito.`
                  : errors[0]
                    ? ownerError(errors[0])
                    : 'Il cervello non ha proposto voci per questo passo. Puoi rifarlo con il pulsante qui sotto.'}
            </div>
            {isManager && (
              <button type="button" className="bkc-link" disabled={redo.isPending} onClick={() => redo.mutate()}>
                {redo.isPending ? 'Lo rifaccio…' : 'Rifai il progetto di questo passo'}
              </button>
            )}
            {redo.error && <div className="why">{ownerError(errorMessage(redo.error))}</div>}
          </section>
        )}
        {step === 'organizzazione' && d?.persone_perche && <p className="bkc-noq">{d.persone_perche}</p>}
        {/* Un passo fallito non ha domande da rivedere: niente «Rivedo le domande» sotto l'errore. */}
        {!(failed && !domande.length) && (
          <Questions step={step} domande={domande} rethinking={step === 'impianti' ? !!eq.data?.in_preparazione.length : undefined} />
        )}
        {ADD[step] && (step === 'software' || step === 'organizzazione' || step === 'procedure') && (
          <AddedItems
            step={step}
            intro={ADD[step]!.intro}
            label={ADD[step]!.label}
            placeholder={ADD[step]!.placeholder}
            notePlaceholder={ADD[step]!.note}
            feminine={ADD[step]!.feminine}
          />
        )}
      </StepFrame>
      {last && <ConfigurationReview />}
    </div>
  );
}
